"use client";

import React, { useState } from "react";
import Script from "next/script";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    workEmail: "",
    designation: "",
    companyName: "",
    phoneNumber: "",
    countryCode: "IND +91",
    teamSize: "",
    teamLocation: "",
    hearAbout: "",
    programs: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error'
  const [touched, setTouched] = useState({});

  const LEAD_API_URL = "https://payloadcms.stayingahead.ai/api/b2b-lead";
  const LEAD_IDENTIFIER = "GROWTHSCHOOL";

  const countryOptions = ["IND +91", "USA +1", "UK +44", "AUS +61", "JPN +81"];

  const hearAboutOptions = [
    "LinkedIn",
    "Social Media",
    "Google",
    "Referral",
    "Other",
  ];

  const teamSizeOptions = [
    "0 to 30",
    "30 to 100",
    "100 to 250",
    "250 and above",
  ];

  const programOptions = [
    "Gen AI Workshop",
    "AI for Marketers",
    "AI for Engineers",
    "AI for Product Managers",
    "AI for Founders and Business",
    "Performance Marketing Mentorship",
    "SEO Mentorship",
    "Digital Marketing",
    "Product Marketing",
    "Growth Hacking",
    "Product Management",
    "UX Design",
    "Cloud Data Engineering",
    "Data Analytics",
    "Management Consulting",
    "D2C Mentorship",
    "Any Other (We can customize it for you)",
  ];

  const personalEmailDomains = [
    "gmail.com",
    "yahoo.com",
    "hotmail.com",
    "outlook.com",
    "icloud.com",
    "aol.com",
    "protonmail.com",
    "mail.com",
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    
    const domain = email.split("@")[1]?.toLowerCase();
    if (personalEmailDomains.includes(domain)) {
      return "Please use your work email. Personal emails like Gmail, Yahoo are not allowed";
    }
    
    return null;
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(phone.replace(/\s/g, ""))) {
      return "Please enter a valid 10-digit phone number";
    }
    return null;
  };

  // Live field-level errors (so users know what's wrong even when the button is disabled)
  const fullNameError =
    !formData.fullName.trim() && touched.fullName
      ? "Full name is required"
      : null;

  const workEmailError = formData.workEmail.trim()
    ? validateEmail(formData.workEmail)
    : touched.workEmail
      ? "Work email is required"
      : null;

  const companyNameError =
    !formData.companyName.trim() && touched.companyName
      ? "Company name is required"
      : null;

  const phoneNumberError = formData.phoneNumber.trim()
    ? validatePhone(formData.phoneNumber)
    : touched.phoneNumber
      ? "Phone number is required"
      : null;

  const teamSizeError =
    !formData.teamSize && touched.teamSize ? "Team size is required" : null;

  const teamLocationError =
    !formData.teamLocation.trim() && touched.teamLocation
      ? "Team location is required"
      : null;

  const programsError =
    !formData.programs && touched.programs ? "Please select a program" : null;

  // Used only for disabling/enabling the button (doesn't set error state)
  const isFormValid =
    !!formData.fullName.trim() &&
    !!formData.workEmail.trim() &&
    !validateEmail(formData.workEmail) &&
    !!formData.companyName.trim() &&
    !!formData.phoneNumber.trim() &&
    !validatePhone(formData.phoneNumber) &&
    !!formData.teamSize &&
    !!formData.teamLocation.trim() &&
    !!formData.programs;

  const validateForm = () => {
    const newErrors = {};

    // Full Name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    // Work Email validation
    if (!formData.workEmail.trim()) {
      newErrors.workEmail = "Work email is required";
    } else {
      const emailError = validateEmail(formData.workEmail);
      if (emailError) {
        newErrors.workEmail = emailError;
      }
    }

    // Company Name validation
    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }

    // Phone Number validation
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else {
      const phoneError = validatePhone(formData.phoneNumber);
      if (phoneError) {
        newErrors.phoneNumber = phoneError;
      }
    }

    // Team Size validation
    if (!formData.teamSize) {
      newErrors.teamSize = "Team size is required";
    }

    // Team Location validation
    if (!formData.teamLocation.trim()) {
      newErrors.teamLocation = "Team location is required";
    }

    // Programs validation
    if (!formData.programs) {
      newErrors.programs = "Please select a program";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Format a human-readable timestamp
  const getFormattedTimestamp = () => {
    const now = new Date();
    return now.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Mark required fields as touched so user can immediately see what's missing
      setTouched((prev) => ({
        ...prev,
        fullName: true,
        workEmail: true,
        companyName: true,
        phoneNumber: true,
        teamSize: true,
        teamLocation: true,
        programs: true,
      }));
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const timestamp = getFormattedTimestamp();
      const payload = { ...formData, timestamp, identifier: LEAD_IDENTIFIER };

      const response = await fetch(LEAD_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Server responded ${response.status}`);
      }

      setSubmitStatus("success");
      
      // Reset form on success
      setFormData({
        fullName: "",
        workEmail: "",
        designation: "",
        companyName: "",
        phoneNumber: "",
        countryCode: "IND +91",
        teamSize: "",
        teamLocation: "",
        hearAbout: "",
        programs: "",
      });

      // Clear validation UI after successful submit
      setErrors({});
      setTouched({});
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Google tag (gtag.js) */}
      <Script
        id="ga4-gtag-src"
        src="https://www.googletagmanager.com/gtag/js?id=G-E27GXNWYGG"
        strategy="afterInteractive"
      />
      <Script id="ga4-gtag-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-E27GXNWYGG');
        `}
      </Script>
      {/* Google Ads conversion helper as requested */}
      <Script id="ads-conversion-helper" strategy="afterInteractive">
        {`
          function gtag_report_conversion(url) {
            var callback = function () {
              if (typeof(url) != 'undefined') {
                window.location = url;
              }
            };
            if (typeof gtag === 'function') {
              gtag('event', 'conversion', {
                  'send_to': 'AW-989468600/tPDSCOijt4oaELiv6NcD',
                  'event_callback': callback
              });
            }
            return false;
          }
          window.gtag_report_conversion = gtag_report_conversion;
        `}
      </Script>
      <div
        className="w-full py-10 md:py-20"
        style={{ backgroundColor: "#FFFAF1" }}
        id="contact"
      >
      <div className="w-[90%] md:w-[80%] mx-auto">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
          {/* Left Side - Text Content */}
          <div className="lg:w-3/5">
            <p className="text-sm md:text-base uppercase font-medium tracking-wide text-gray-600 mb-4">
              INVEST IN GROWTH
            </p>
            <h2 className="text-2xl w-full md:w-[60%] md:text-[36px] font-medium leading-tight text-gray-900">
              Ready to Start Your Growth Journey?
            </h2>
          </div>

          {/* Right Side - Form */}
          <div className="lg:w-2/5">
          <p className="text-sm md:text-base uppercase font-medium tracking-wide text-gray-600 mb-4">
              Corporate Training Inquiry Form
            </p>
              <form
                onSubmit={handleSubmit}
                className="space-y-6"
                id="contactForm"
              >
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name*
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                    onChange={(e) =>
                      handleInputChange("fullName", e.target.value)
                    }
                  placeholder="Enter your full name"
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors ${
                      fullNameError ? "border-red-500" : "border-gray-300"
                  }`}
                />
                  {fullNameError && (
                    <p className="mt-1 text-sm text-red-600">{fullNameError}</p>
                )}
              </div>

              {/* Work Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Work Email*
                </label>
                <input
                  type="email"
                  value={formData.workEmail}
                    onChange={(e) =>
                      handleInputChange("workEmail", e.target.value)
                    }
                  placeholder="Enter your work email"
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors ${
                      workEmailError ? "border-red-500" : "border-gray-300"
                  }`}
                />
                  {workEmailError && (
                    <p className="mt-1 text-sm text-red-600">
                      {workEmailError}
                    </p>
                )}
              </div>

              {/* Designation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Designation
                </label>
                <input
                  type="text"
                  value={formData.designation}
                    onChange={(e) =>
                      handleInputChange("designation", e.target.value)
                    }
                  placeholder="Enter your designation"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors"
                />
              </div>

              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name*
                </label>
                <input
                  type="text"
                  value={formData.companyName}
                    onChange={(e) =>
                      handleInputChange("companyName", e.target.value)
                    }
                  placeholder="Enter your company name"
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors ${
                      companyNameError ? "border-red-500" : "border-gray-300"
                  }`}
                />
                  {companyNameError && (
                    <p className="mt-1 text-sm text-red-600">
                      {companyNameError}
                    </p>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number*
                </label>
                <div className="flex gap-3">
                  <select
                    value={formData.countryCode}
                      onChange={(e) =>
                        handleInputChange("countryCode", e.target.value)
                      }
                    className="px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none "
                  >
                    {countryOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                      onChange={(e) =>
                        handleInputChange("phoneNumber", e.target.value)
                      }
                    placeholder="Enter phone number"
                    className={`flex-1 px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors ${
                        phoneNumberError ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                </div>
                  {phoneNumberError && (
                    <p className="mt-1 text-sm text-red-600">
                      {phoneNumberError}
                    </p>
                )}
              </div>

                {/* Team Size and Team Location Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Team Size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Size*
                  </label>
                  <select
                    value={formData.teamSize}
                      onChange={(e) =>
                        handleInputChange("teamSize", e.target.value)
                      }
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none  transition-colors ${
                        teamSizeError ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <option value="">Select team size</option>
                    {teamSizeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                    {teamSizeError && (
                      <p className="mt-1 text-sm text-red-600">
                        {teamSizeError}
                      </p>
                    )}
                  </div>

                  {/* Team Location */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Team Location*
                    </label>
                    <input
                      type="text"
                      value={formData.teamLocation}
                      onChange={(e) =>
                        handleInputChange("teamLocation", e.target.value)
                      }
                      placeholder="Enter team location"
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors ${
                        teamLocationError ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {teamLocationError && (
                      <p className="mt-1 text-sm text-red-600">
                        {teamLocationError}
                      </p>
                  )}
                  </div>
                </div>

                {/* Programs */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Program(s) you looking for*
                  </label>
                  <select
                    value={formData.programs}
                    onChange={(e) =>
                      handleInputChange("programs", e.target.value)
                    }
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none  transition-colors ${
                      programsError ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <option value="">Select program</option>
                    {programOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {programsError && (
                    <p className="mt-1 text-sm text-red-600">{programsError}</p>
                  )}
              </div>

              {/* Where did you hear about us */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Where did you hear about us?
                </label>
                <select
                  value={formData.hearAbout}
                    onChange={(e) =>
                      handleInputChange("hearAbout", e.target.value)
                    }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none "
                >
                  <option value="">Select an option</option>
                  {hearAboutOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                  disabled={!isFormValid || isSubmitting}
                  onClick={() => {
                    if (
                      typeof window !== "undefined" &&
                      typeof window.gtag_report_conversion === "function"
                    ) {
                      try {
                        window.gtag_report_conversion();
                      } catch (e) {}
                    }
                  }}
                  className={`bg-[#33c375] shadow-[inset_0_0_0_2px_rgba(0,39,38,0.3)] text-[#012726] font-medium px-6 py-3 rounded-xl stroke-2 flex items-center justify-center w-full transition-all ${
                    !isFormValid || isSubmitting
                      ? "opacity-60 cursor-not-allowed"
                      : "cursor-pointer hover:bg-[#6AD384]"
                  }`}
              >
                  {isSubmitting ? "Submitting..." : "Request a Callback"}
              </button>

              {/* Status messages */}
              {submitStatus === "success" && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-green-800 text-sm font-medium">
                      ✅ Form submitted successfully! We'll get back to you
                      soon.
                  </p>
                </div>
              )}
              {submitStatus === "error" && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-red-800 text-sm font-medium">
                    ❌ Something went wrong. Please try again.
                  </p>
                </div>
              )}
            </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactForm;
